<script setup lang="ts">
import type { GetDonationBoxResult } from '~/types/donations'
import { useWebSocket } from '~/composables/useWebSocket'
import type { WebSocketsModelReply } from '~/types/webSockets'
import { formatCurrency } from '~/utils/formatters'
import type { WebsiteDonationBox } from '#shared/types/donations'
import { getRandomHeroImage } from '~/utils/helpers'
import type { ImageProps } from '~/types/images'

interface DonationBoxProps {
  data: GetDonationBoxResult | null | undefined
  preview?: boolean
}

const props = defineProps<DonationBoxProps>()

const { t } = useI18n('components.organisms.donation.donation-box')

const url = useRequestURL()

const route = useRoute()

const hasLargeDonationGoalAmounts = computed(
  () =>
    (props.data?.donationBox?.donationGoalAmount ?? 0) >= 10000 &&
    (props.data?.donationStatistics?.totalAmount ?? 0) >= 10000
)

const heroImage = computed<ImageProps>(() => {
  if (!props.data) {
    return getRandomHeroImage(route.path).value
  }
  const coverPhoto = props.data.donationBox.coverPhoto
  return {
    src: coverPhoto.url,
    // Alts are set to filenames, which are useless at the moment
    alt: {
      nl: 'Actie foto',
      en: 'Fundraiser photo'
    }
  }
})

if (import.meta.client) {
  const { setChannel } = useWebSocket({
    event: 'Donation\\NewDonationBoxDonation',
    callback: (newDonationBoxAmount: WebSocketsModelReply<WebsiteDonationBox>) => {
      if (props.data) {
        updateModel(props.data.donationStatistics, newDonationBoxAmount.model)
      }
    },
    channel: `tg-website-specific-channel-donation-box-${props.data?.donationBox.id}`,
    isPublic: true
  })!
  watch(
    () => props.data,
    (newValue) => {
      if (newValue && !props.preview) {
        setChannel(`tg-website-specific-channel-donation-box-${newValue.donationBox.id}`)
      }
    },
    { immediate: true }
  )
}
</script>

<template>
  <NuxtLayout
    :class="preview && 'preview'"
    name="default"
    :title="data?.donationBox.title"
    :image="heroImage"
    :hide-head="preview"
    :hide-footer="preview"
    :ui="{
      subtitle: 'w-full'
    }"
  >
    <template #subtitle>
      <div class="hidden w-full lg:block">
        <h3 class="text-h3">
          {{ t('.quote-title') }}
        </h3>
        <q class="mb-6! text-justify font-bold wrap-break-word text-default">
          {{ data?.donationBox.description }}
        </q>
      </div>
      <div class="w-full lg:mb-6!">
        <div class="flex flex-col text-left">
          <div>
            <span
              class="mt-2! mb-0! text-title leading-[1.3rem] font-semibold text-primary dark:text-success"
            >
              {{ formatCurrency(data?.donationStatistics.totalAmount) }}
            </span>
            <span
              v-if="!hasLargeDonationGoalAmounts"
              class="ml-2! text-lg text-gray-800 dark:text-default"
            >
              {{
                t('.labels.target', {
                  target: formatCurrency(data?.donationBox.donationGoalAmount)
                })
              }}
            </span>
          </div>
          <span
            v-if="hasLargeDonationGoalAmounts"
            class="ml-1! text-lg text-gray-800 dark:text-default"
          >
            {{
              t('.labels.target', {
                target: formatCurrency(data?.donationBox.donationGoalAmount)
              })
            }}
          </span>
          <div v-if="data?.donationStatistics.totalDonations || preview" class="ml-1!">
            <span
              class="mt-2! mb-0! text-lg leading-[1.3rem] font-semibold text-primary dark:text-success"
            >
              {{ data?.donationStatistics.totalDonations }}
            </span>
            <span class="ml-1! text-lg text-gray-800 dark:text-default">
              {{ t('.labels.donations', data?.donationStatistics.totalDonations ?? 0) }}
            </span>
          </div>
        </div>
        <VProgress
          :max="data?.donationBox.donationGoalAmount"
          :value="data?.donationStatistics.totalAmount"
          size="sm"
          class="mt-1"
        />
      </div>
    </template>

    <template #buttons>
      <ShareButton
        v-if="data && !preview"
        :title="data.donationBox.title"
        :description="data.donationBox.description"
        :cover-photo="data.donationBox.coverPhoto"
        :link="url.href"
        :disabled="preview"
      />
    </template>

    <VSection title=".quote-title" class="lg:hidden">
      <p class="mb-6! text-center wrap-break-word">
        <span class="text-title">“</span>
        {{ data?.donationBox.description }}
        <span class="text-title">”</span>
      </p>
    </VSection>

    <VSection>
      <template #content>
        <div class="columns max-lg:flex max-lg:flex-col">
          <div class="column hidden half lg:block">
            <div class="prose text-justify">
              <h2>
                {{ t('.description-title') }}
              </h2>

              <p>
                {{ t('.section-one') }}
              </p>
              <p>
                {{ t('.section-two') }}
              </p>
              <p>
                {{ t('.section-three') }}
              </p>
            </div>

            <RecentDonations
              v-if="
                data?.donationStatistics?.recentDonations &&
                data.donationStatistics.recentDonations.length > 0
              "
              :recent-donations="data.donationStatistics.recentDonations"
            />
          </div>
          <div
            v-if="
              data?.donationStatistics?.recentDonations &&
              data.donationStatistics.recentDonations.length > 0
            "
            class="mb-2 column full lg:hidden"
          >
            <RecentDonations
              :recent-donations="data.donationStatistics.recentDonations"
            />
          </div>

          <div v-if="data" class="column full lg:half">
            <DonationForm
              type="box"
              :slug="data.donationBox.slug"
              :donation-box-id="data.donationBox.id"
              :disabled="preview"
              :donation-amounts="data.donationBox.donationAmountOptions"
            />
          </div>

          <div class="prose mt-4! column max-w-none text-justify full lg:hidden">
            <h2>
              {{ t('.description-title') }}
            </h2>

            <p>
              {{ t('.section-one') }}
            </p>
            <p>
              {{ t('.section-two') }}
            </p>
            <p>
              {{ t('.section-three') }}
            </p>
          </div>
        </div>
      </template>
    </VSection>
  </NuxtLayout>
</template>

<style scoped></style>
